import type { InjectionKey, Ref } from 'vue'

import { useBaseDashboard } from './apps/MainApp/views/Dashboard/Base/core/builder'
import useRecomputedValues from './apps/ValuerTool/views/Valuation/recomputedValues'
import useAppointment from './composables/useAppointment'
import { useCurUser } from './composables/useCurUser'
import { useFlow } from './composables/useFlow'
import { useListingReferences } from './composables/useListingReferences'
import usePayment from './composables/usePayment'
import { useRequest } from './composables/useRequest'
import { useRequestList } from './composables/useRequestList'
import useTalkjs from './composables/useTalk'
import useTracker from './composables/useTracker'
import useTransactionValue from './composables/useTransactionValue'
import type { ContactForm } from './types/contactForm'
import type { WrappedRequest } from './types/curRequest'

interface DashboardInstallerOptions {
  showDuplicates: (row: WrappedRequest) => void
  dateColor: (date: string | Date) => string | false
  requests: ReturnType<typeof useRequestList>
  colFilters: Record<string, unknown>
  curUser?: ReturnType<typeof useCurUser>
}

type DashboardInstaller = (
  dashboard: ReturnType<typeof useBaseDashboard>,
  options: DashboardInstallerOptions
) => void

// READ for provide/inject typescript safety: https://awad.dev/blog/type-safe-provide-inject/
export const contactFormKey: InjectionKey<ContactForm> = Symbol('contactForm')
export const curAppointmentKey: InjectionKey<ReturnType<typeof useAppointment>> = Symbol('curAppointment')
export const curPaymentKey: InjectionKey<ReturnType<typeof usePayment>> = Symbol('curPayment')
export const curRequestKey: InjectionKey<ReturnType<typeof useRequest>> = Symbol('curRequest')
export const curTransactionKey: InjectionKey<ReturnType<typeof useTransactionValue>> =
  Symbol('curTransaction')
export const dashboardInstallerKey: InjectionKey<DashboardInstaller> = Symbol('dashboardInstaller')
export const ersRequestKey: InjectionKey<ReturnType<typeof useRequest>> = Symbol('ersRequest')
export const flowKey: InjectionKey<ReturnType<typeof useFlow>> = Symbol('flow')
export const formatKey = Symbol('format')
export const isRealEpcKey = Symbol('isRealEpc')
export const recomputedValuesKey: InjectionKey<ReturnType<typeof useRecomputedValues>> =
  Symbol('recomputedValues')
export const referencesKey: InjectionKey<ReturnType<typeof useListingReferences>> = Symbol('references')
export const requestListKey: InjectionKey<ReturnType<typeof useRequestList>> = Symbol('requestList')
export const talkjsKey: InjectionKey<ReturnType<typeof useTalkjs>> = Symbol('talkjs')
export const trackerKey: InjectionKey<ReturnType<typeof useTracker>> = Symbol('tracker')
export const actionButtonsKey = Symbol('actionButtons')
export const reportOverridesKey: InjectionKey<{
  displayedValuationType: string
  hideInteriorPage: boolean
}> = Symbol('reportOverrides')
export const isInternalRemarksAttachedKey: InjectionKey<Ref<boolean>> = Symbol('isInternalRemarksAttached')
